import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { DataRoomRedFlag, RedFlagStatus } from "@/models/data-room/DataRoomRedFlag";
import { DataRoomLazoModalSx } from "@/pages/OneDataRoomPage";
import RedFlagService from "@/services/RedFlagService";
import { AddOutlined } from "@mui/icons-material";
import { Box, Typography, styled, typographyClasses } from "@mui/material";
import { CSSProperties, useEffect, useState } from "react";
import { StyledInputField } from "../fundraising/advanced-search/AdvancedSearchFilters";
import CommonButton, { CommonButtonSecondarySx } from "../utils/CommonButton";
import LazoModal from "../utils/LazoModal";
import { DATA_ROOM_STATIC_ID } from "./DataRoomCategoriesDefines";
import DataRoomRedFlagsEmptyScreen from "./DataRoomRedFlagsEmptyScreen";
import DataRoomRedFlagsTable from "./DataRoomRedFlagsTable";

enum TabType {
  ACTIVE,
  DISMISSED,
}

type FlagGroup = {
  [k in TabType]: DataRoomRedFlag[];
};

export default function DataRoomRedFlagsSection() {
  const [tab, setTab] = useState<TabType>(TabType.ACTIVE);
  const [flags, setFlags] = useState<DataRoomRedFlag[]>([]);
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const [newName, setName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  const [flagGroup, setFlagGroup] = useState<FlagGroup>({} as FlagGroup);

  function handleKeyPress(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCreate();
    }
  }

  useEffect(() => {
    setLoading(true);
    RedFlagService.getAll(DATA_ROOM_STATIC_ID.MASTER)
      .then((r) => r.onSuccess((flags) => setFlags(flags)))
      .finally(() => setLoading(false));
  }, []);

  function group(flags: DataRoomRedFlag[]) {
    let groups: FlagGroup = {
      [TabType.ACTIVE]: [],
      [TabType.DISMISSED]: [],
    };
    flags.forEach((f) => {
      switch (f.status) {
        case RedFlagStatus.ASSIGNED:
        case RedFlagStatus.CREATED:
          groups[TabType.ACTIVE].push(f);
          break;
        case RedFlagStatus.DISMISSED:
        case RedFlagStatus.FIXED:
          groups[TabType.DISMISSED].push(f);
          break;
        default:
          break;
      }
    });
    setFlagGroup(groups);
  }

  function handleCreate() {
    if (creating) return;
    if (!newName || newName === "") {
      return;
    }

    setCreating(true);
    RedFlagService.createRedFlag({ name: newName } as DataRoomRedFlag)
      .then((r) => {
        r.onSuccess((flag) => {
          setFlags((prev) => [...prev, flag]);
          setOpenCreateModal(false);
          setName("");
        }).onError((e) => {
          NotificationService.error("Couldn't create red flag, please try again");
          console.log("error: " + e);
        });
      })
      .finally(() => setCreating(false));
  }

  useEffect(() => {
    group(flags);
  }, [flags]);

  return (
    <>
      <Header>
        <Row gap="1.5rem">
          <Typography fontSize="0.875rem" fontWeight="600" lineHeight="20px">
            Red Flags
          </Typography>
          <CommonButton sx={CommonButtonSecondarySx} style={CustomButtonStyle} onClick={() => setOpenCreateModal(true)}>
            <AddOutlined />
            Add
          </CommonButton>
        </Row>
        <Row sx={{ borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}` }}>
          <DataRoomTabContainer current={tab === TabType.ACTIVE} onClick={() => setTab(TabType.ACTIVE)}>
            <Typography className="text">Active</Typography>
            {flagGroup[TabType.ACTIVE]?.length > 0 && (
              <DataRoomCountCircle>
                <Typography>{flagGroup[TabType.ACTIVE].length}</Typography>
              </DataRoomCountCircle>
            )}
          </DataRoomTabContainer>
          <DataRoomTabContainer current={tab === TabType.DISMISSED} onClick={() => setTab(TabType.DISMISSED)}>
            <Typography className="text">Dismissed</Typography>
            {flagGroup[TabType.DISMISSED]?.length > 0 && (
              <DataRoomCountCircle>
                <Typography>{flagGroup[TabType.DISMISSED].length}</Typography>
              </DataRoomCountCircle>
            )}
          </DataRoomTabContainer>
        </Row>
      </Header>
      {loading && <Spinner size={"2rem"} style={{ color: Colors.LAZO_PURPLE }} />}
      {!loading ? (
        flagGroup[tab]?.length === 0 ? (
          <DataRoomRedFlagsEmptyScreen />
        ) : (
          <DataRoomRedFlagsTable flags={flagGroup[tab]} onFlagsUpdate={(updated) => setFlags(updated)} />
        )
      ) : (
        <></>
      )}
      <LazoModal
        sx={DataRoomLazoModalSx}
        open={openCreateModal}
        title={"Add Red Flag"}
        handleClose={() => setOpenCreateModal(false)}
        buttons={[
          <CommonButton
            style={{ borderRadius: "0.5rem" }}
            purpleBorderVariant
            onClick={() => setOpenCreateModal(false)}
            key={"cancel-button"}
          >
            Cancel
          </CommonButton>,
          <CommonButton
            style={{ borderRadius: "0.5rem" }}
            onClick={handleCreate}
            disabled={creating || !newName || newName === ""}
            key={"save-button"}
          >
            {!creating ? "Save" : <Spinner size={"1.5rem"} />}
          </CommonButton>,
        ]}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            gap: "0.25rem",
          }}
        >
          <Typography fontSize="0.75rem" fontWeight="400" lineHeight="18px" paddingLeft="0.5rem" color={Colors.LIGHT_GRAY_8A8E93}>
            Red Flag Name
          </Typography>
          <StyledInputField
            sx={{ "& .MuiInputBase-root": { padding: 0 } }}
            hideIcon
            value={newName}
            onChange={(v) => setName(v.currentTarget.value)}
            placeholder="Type new name..."
            onKeyDown={handleKeyPress}
          />
        </Box>
      </LazoModal>
    </>
  );
}

const Header = styled(Box)({
  display: "flex",
  justifyContent: "center",
  gap: "1rem",
  alignSelf: "stretch",
  flexDirection: "column",
  width: "100%",
});

const CustomButtonStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.25rem",
  padding: "0.5rem",
  borderRadius: "0.25rem",
  height: "28px",
};

const Row = styled(Box)({
  display: "flex",
  alignItems: "center",
});

export const DataRoomTabContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "current",
})<{ current: boolean }>(({ current }) => ({
  display: "flex",
  gap: "0.5rem",
  position: "relative",
  alignItems: "center",
  padding: "0.75rem",
  transition: "background-color 0.15s ease",
  "&.text": {
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: "19.6px",
  },
  ":hover": {
    cursor: "pointer",
    backgroundColor: Colors.ALMOST_WHITE,
  },
  ...(current
    ? {
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          display: "inline-block",
          backgroundColor: Colors.BLACK_2,
          width: "100%",
          height: "1px",
          transition: "width 0.2s ease",
        },
      }
    : {
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          display: "inline-block",
          width: "0",
          height: "0",
          transition: "width 0.2s ease",
        },
      }),
}));

export const DataRoomCountCircle = styled(Box)({
  display: "flex",
  borderRadius: "100px",
  padding: "0.25rem",
  gap: "0.5rem",
  height: "20px",
  width: "20px",
  backgroundColor: Colors.DARK_GREY_3E3E3E,
  color: Colors.WHITE,
  [`& .${typographyClasses.root}`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    lineHeight: "12px",
    fontWeight: "700",
    width: "100%",
  },
});
